import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ClassroomSummary, DashboardSession, PointsEntry, TeacherDashboard } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { shortDate } from './shared';

/** Motivos rápidos para dar o quitar puntos. */
const REASONS = ['Participación', 'Versículo memorizado', 'Tarea', 'Trajo su Biblia', 'Conducta'] as const;
const pct = (a: { present: number; total: number }) => (a.total ? Math.round((a.present / a.total) * 100) : null);

/**
 * Panel del maestro (Raíces y Raíces Kids): la clase de hoy con sus accesos, asistencia, puntos y
 * balance de cada alumno, y sus clases. Todo lo demás sigue en «Abrir el salón completo».
 */
export function TeacherPanelPage() {
  const { classroomId } = useParams();
  const me = useMe();
  const navigate = useNavigate();
  const churchId = me.activeChurchId;
  const rooms = useAsync(
    (signal) =>
      churchId
        ? api.get<{ items: ClassroomSummary[] }>(`/organization/churches/${churchId}/classrooms`, signal).then((r) => {
            const teach = r.items.filter((c) => c.role === 'teacher');
            return teach.length ? teach : r.items.filter((c) => c.role === 'manager');
          })
        : Promise.resolve([]),
    [churchId],
  );
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (rooms.status === 'loading') return <div className="page"><Loading /></div>;
  if (rooms.status === 'error') return <div className="page"><ErrorState error={rooms.error} onRetry={rooms.reload} /></div>;
  if (rooms.data.length === 0) {
    return (
      <div className="page">
        <h1>Panel del maestro</h1>
        <Card>
          <EmptyState title="No enseñas en ningún salón todavía." />
        </Card>
      </div>
    );
  }
  // Con un solo salón (o sin elegir), entra directo al primero.
  if (!classroomId || !rooms.data.some((r) => r.id === classroomId)) return <Navigate to={`/app/salones/panel/${rooms.data[0]!.id}`} replace />;
  return (
    <div className="page teacher-panel">
      <p className="small">
        <Link to={`/app/salones/${classroomId}`}>← {rooms.data.find((r) => r.id === classroomId)!.name}</Link>
      </p>
      <header className="page__header">
        <h1>Panel del maestro</h1>
        {rooms.data.length > 1 ? (
          <div className="field">
            <label htmlFor="panel-room">Salón</label>
            <select id="panel-room" value={classroomId} onChange={(e) => navigate(`/app/salones/panel/${e.target.value}`)}>
              {rooms.data.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </header>
      <Dashboard key={classroomId} base={`/organization/churches/${churchId}/classrooms/${classroomId}`} classroomId={classroomId} />
    </div>
  );
}

function Dashboard({ base, classroomId }: { base: string; classroomId: string }) {
  const data = useAsync((signal) => api.get<TeacherDashboard>(`${base}/dashboard`, signal), [base]);
  const [open, setOpen] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const d = data.data;
  const room = `/app/salones/${classroomId}`;
  const now = Date.now();
  const upcoming = d.sessions.filter((x) => new Date(x.endsAt).getTime() >= now).reverse();
  const past = d.sessions.filter((x) => new Date(x.endsAt).getTime() < now);
  const absent = d.students
    .filter((s) => s.attendance.total > 0 && s.attendance.present < s.attendance.total)
    .sort((a, b) => b.attendance.total - b.attendance.present - (a.attendance.total - a.attendance.present))
    .slice(0, 3);

  return (
    <div className={d.room.kids ? 'kids' : undefined}>
      {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
      <TodayCard s={d.today} room={room} />

      <section className="panel-stats" aria-label="Resumen del mes">
        <div className="panel-stat">
          <strong>{d.summary.monthClasses}</strong>
          <span>clases con lista este mes</span>
        </div>
        <div className="panel-stat">
          <strong>{d.summary.monthPresent}</strong>
          <span>asistencias</span>
        </div>
        <div className="panel-stat">
          <strong>{d.summary.monthAbsent}</strong>
          <span>faltas</span>
        </div>
        <Link className="panel-stat" to={`${room}?ver=temario`}>
          <strong>{d.summary.toGrade}</strong>
          <span>respuestas por calificar</span>
        </Link>
      </section>
      {d.summary.drafts ? (
        <p className="small">
          Tienes {d.summary.drafts === 1 ? '1 cuestionario en borrador' : `${d.summary.drafts} cuestionarios en borrador`}: <Link to={`${room}?ver=temario`}>revisar y publicar</Link>.
        </p>
      ) : null}
      {absent.length ? (
        <p className="small">
          Con más faltas: {absent.map((s) => `${s.person.name} (${s.attendance.total - s.attendance.present})`).join(', ')}.
        </p>
      ) : null}

      <Card title={`Alumnos (${d.students.length})`}>
        {d.students.length === 0 ? <p className="muted">El salón aún no tiene alumnos.</p> : null}
        <div id="alumnos" className="panel-students">
          {d.students.map((s) => (
            <StudentRow
              key={s.person.personId}
              s={s}
              base={base}
              sessionId={d.today?.id ?? null}
              open={open === s.person.personId}
              onToggle={() => setOpen(open === s.person.personId ? null : s.person.personId)}
              onChange={(text) => {
                setMessage(text ? { tone: 'success', text } : null);
                data.reload();
              }}
              onError={(text) => setMessage({ tone: 'error', text })}
            />
          ))}
        </div>
      </Card>

      <Card title="Mis clases">
        {upcoming.length ? <h3 className="small">Próximas</h3> : null}
        <ul className="list">{upcoming.slice(0, 4).map((x) => <SessionItem key={x.id} x={x} room={room} upcoming />)}</ul>
        {past.length ? <h3 className="small">Pasadas</h3> : null}
        <ul className="list">{past.slice(0, 8).map((x) => <SessionItem key={x.id} x={x} room={room} />)}</ul>
        <p className="small">
          <Link to={room}>Abrir el salón completo</Link> (temario, materiales, avisos, alumnos y todos los cuestionarios).
        </p>
      </Card>
    </div>
  );
}

function TodayCard({ s, room }: { s: DashboardSession | null; room: string }) {
  if (!s) {
    return (
      <Card title="Clase de hoy">
        <p className="muted">No hay clases programadas.</p>
        <Link className="btn btn--secondary" to={`${room}?ver=clases`}>
          Programar clases
        </Link>
      </Card>
    );
  }
  const isToday = new Date(s.startsAt).toDateString() === new Date().toDateString();
  const to = `${room}/clases/${s.id}`;
  return (
    <Card title={isToday ? 'Clase de hoy' : 'Próxima clase'}>
      <p>
        <strong>{s.topic ?? 'Clase'}</strong> · {shortDate(s.startsAt)}
      </p>
      <p className="small muted">
        {s.attendanceTaken ? `Asistencia tomada (${s.present} presentes)` : 'Falta tomar asistencia'} · {s.hasNotes ? 'Con notas' : 'Sin notas'} · {s.hasQuiz ? 'Con cuestionario' : 'Sin cuestionario'}
      </p>
      <div className="panel-actions">
        <Link className="btn btn--primary" to={`${to}#asistencia`}>
          Tomar asistencia
        </Link>
        <Link className="btn btn--secondary" to={`${to}?editar=1`}>
          Notas
        </Link>
        <Link className="btn btn--secondary" to={`${to}#cuestionario`}>
          Cuestionario
        </Link>
        <a className="btn btn--secondary" href="#alumnos">
          Dar puntos
        </a>
      </div>
    </Card>
  );
}

function SessionItem({ x, room, upcoming = false }: { x: DashboardSession; room: string; upcoming?: boolean }) {
  return (
    <li className="list__item">
      <Link className="list__link" to={`${room}/clases/${x.id}`}>
        <strong>{x.topic ?? 'Clase'}</strong>
        <span className="small muted">
          {shortDate(x.startsAt)}
          {x.canceled ? ' · Cancelada' : ''}
          {!x.canceled && !upcoming ? (x.attendanceTaken ? ` · ✓ Asistencia (${x.present})` : ' · ⚠ Falta asistencia') : ''}
          {x.hasQuiz ? ' · Cuestionario ✓' : ''}
          {x.badVerses ? ' · ⚠ Versículos por corregir' : ''}
        </span>
      </Link>
    </li>
  );
}

function StudentRow({
  s,
  base,
  sessionId,
  open,
  onToggle,
  onChange,
  onError,
}: {
  s: TeacherDashboard['students'][number];
  base: string;
  sessionId: string | null;
  open: boolean;
  onToggle: () => void;
  onChange: (message: string | null) => void;
  onError: (message: string) => void;
}) {
  const [pending, setPending] = useState<number | null>(null);
  const [other, setOther] = useState('');
  const [busy, setBusy] = useState(false);
  const history = useAsync((signal) => (open ? api.get<{ items: PointsEntry[] }>(`${base}/points?personId=${s.person.personId}`, signal).then((r) => r.items) : Promise.resolve([])), [open, base, s.points]);
  const attendance = pct(s.attendance);

  async function give(points: number, reason: string) {
    setBusy(true);
    try {
      await api.post(`${base}/points`, { personId: s.person.personId, points, reason, sessionId });
      setPending(null);
      setOther('');
      onChange(`${points > 0 ? '+' : ''}${points} a ${s.person.name} · ${reason}`);
    } catch (e) {
      onError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function undo(id: string) {
    setBusy(true);
    try {
      await api.delete(`${base}/points/${id}`);
      onChange('Movimiento deshecho.');
    } catch (e) {
      onError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel-student">
      <div className="panel-student__row">
        <button type="button" className="panel-student__name" aria-expanded={open} onClick={onToggle}>
          <strong>{s.person.name}</strong>
          <span className="small muted">
            {attendance !== null ? `Asistencia ${attendance}%` : 'Sin listas aún'} · {s.points} pts
          </span>
        </button>
        <div className="panel-student__points" role="group" aria-label={`Puntos de ${s.person.name}`}>
          {[1, 5, -1].map((n) => (
            <button key={n} type="button" className={`points-btn${n < 0 ? ' points-btn--minus' : ''}${pending === n ? ' points-btn--on' : ''}`} onClick={() => setPending(pending === n ? null : n)} aria-label={`${n > 0 ? 'Dar' : 'Quitar'} ${Math.abs(n)} a ${s.person.name}`}>
              {n > 0 ? `+${n}` : `−${-n}`}
            </button>
          ))}
        </div>
      </div>
      {pending !== null ? (
        <div className="panel-student__reasons">
          <span className="small">
            {pending > 0 ? `+${pending}` : `−${-pending}`} por:
          </span>
          {REASONS.map((r) => (
            <button key={r} type="button" className="chip" disabled={busy} onClick={() => void give(pending, r)}>
              {r}
            </button>
          ))}
          <form
            className="panel-student__other"
            onSubmit={(e) => {
              e.preventDefault();
              if (other.trim()) void give(pending, other.trim());
            }}
          >
            <input aria-label="Otro motivo" placeholder="Otro motivo" value={other} maxLength={120} onChange={(e) => setOther(e.target.value)} />
            <Button type="submit" variant="secondary" busy={busy} disabled={!other.trim()}>
              Guardar
            </Button>
          </form>
        </div>
      ) : null}
      {open ? (
        <div className="panel-student__card">
          <dl className="details">
            <dt>Asistencia</dt>
            <dd>{s.attendance.total ? `${s.attendance.present} de ${s.attendance.total} clases (${attendance}%)` : 'Aún no hay listas'}</dd>
            <dt>Puntos</dt>
            <dd>
              {s.points} ({s.quizPoints} de cuestionarios{s.manualPoints ? `, ${s.manualPoints > 0 ? '+' : ''}${s.manualPoints} del maestro` : ''})
            </dd>
            <dt>Promedio</dt>
            <dd>{s.average !== null ? `${s.average}%` : '—'}</dd>
            <dt>Pendientes</dt>
            <dd>{s.pendingQuizzes ? `${s.pendingQuizzes} ${s.pendingQuizzes === 1 ? 'cuestionario sin contestar' : 'cuestionarios sin contestar'}` : 'Nada pendiente'}</dd>
          </dl>
          <h3 className="small">Movimientos de puntos</h3>
          {history.status === 'ready' && history.data.length === 0 ? <p className="small muted">Aún no tiene movimientos.</p> : null}
          {history.status === 'ready' ? (
            <ul className="list">
              {history.data.map((h) => (
                <li key={h.id} className="list__item list__item--actions">
                  <span>
                    <strong className={h.points < 0 ? 'text-danger' : undefined}>
                      {h.points > 0 ? '+' : ''}
                      {h.points}
                    </strong>{' '}
                    {h.reason}
                    <span className="small muted block">
                      {h.by.name} · {shortDate(h.at)}
                    </span>
                  </span>
                  <Button variant="ghost" busy={busy} onClick={() => void undo(h.id)}>
                    Deshacer
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
